import { Component, Input, OnInit } from '@angular/core';
import { LoadingController, PopoverController } from 'ionic-angular';
import { Rodeo } from '../../../../../model/rodeo';
import { PayoutSourceModal } from './payout-source';
import { PayoutConfig } from '../../../../../model/payoutConfig';
import { PayoutSource } from '../../../../../model/payout-source';
import { RodeoService } from '../../../../../services/rodeo.service';
import { AlertMessager } from '../../../../../lib/alertMessager';
import { PayoutConfigPointSeriesDto, PointSerieListItem } from 'model/points/PointSeriesListItem';
import { PointSeriesService } from '../../../../../services/pointseries.service';
import { PointSeries } from '../../../../../model/point-series';

@Component({
    selector: 'staff-payouts',
    templateUrl: 'payouts.html'
})
export class PayoutsSection implements OnInit {
    @Input() set rodeo(rodeo: Rodeo) {
        this._rodeo = rodeo;
        this.filterResults();
    }

    get rodeo() {
        return this._rodeo;
    }

    _rodeo: Rodeo;
    toRounds: number[];
    popoverActive: boolean;

    filterToRodeoEvent: any = 'All';

    payoutConfigsToShow: PayoutConfig[] = [];

    pointSeriesIds: {[id: number]: number[]} = {}
    activePointSeries: PointSerieListItem[];
    rodeoPayoutConfigPointSeries: PayoutConfigPointSeriesDto[];

    constructor(private popoverController: PopoverController, private rodeoService: RodeoService,
        private loadController: LoadingController, private alertMessager: AlertMessager,
        private pointSeriesService: PointSeriesService
    ) {
        this.filterResults();
    }

    async ngOnInit(): Promise<void> {
        let loader = this.loadController.create({
            content: "Loading payout point series data"
        });
        loader.present();
        this.activePointSeries = await this.pointSeriesService.getActiveOrganizationPointSeries(this.rodeo.organization.id, this.rodeo.id);
        this.rodeoPayoutConfigPointSeries = await this.pointSeriesService.getRodeoPayoutConfigPointSeries(this.rodeo.id);
        this.rodeo.payoutConfigs.forEach(pc => {
            const pointSeries = this.rodeoPayoutConfigPointSeries.filter(pcs => pcs.payoutConfigId === pc.id);
            this.pointSeriesIds[pc.id] = pointSeries.map(ps => ps.pointSeriesId);
        })
        loader.dismiss();
    }


    filterResults() {
        if (!this.rodeo)
            return;

        this.payoutConfigsToShow = [];

        this.rodeo.payoutConfigs.forEach((payout) => {
            if (this.filterToRodeoEvent == 'All' || payout.sources.some(x => x.rodeoEventId == this.filterToRodeoEvent.id)) {
                this.payoutConfigsToShow.push(payout);
            }
        })
    }

    updateManualPayouts(payoutConfig: PayoutConfig) {
        if (!payoutConfig.manualPayoutsArray)
            payoutConfig.manualPayoutsArray = [];

        for (var i = 0; i < payoutConfig.places; i++) {
            if (!payoutConfig.manualPayoutsArray[i])
                payoutConfig.manualPayoutsArray.push(0);
        }

        payoutConfig.manualPayoutsArray = payoutConfig.manualPayoutsArray.slice(0, payoutConfig.places);
    }

    addNewPayout() {
        if (!this.rodeo.payoutConfigs)
            this.rodeo.payoutConfigs = [];

        let loader = this.loadController.create({
            content: "Adding new payout..."
        });
        loader.present();

        var newPayoutConfig = new PayoutConfig();
        newPayoutConfig.rodeoId = this.rodeo.id;

        this.rodeoService.addPayoutConfig(newPayoutConfig)
            .then(data => {
                loader.dismiss();

                if ((data as any).message != null && (data as any).message != undefined) {
                    this.alertMessager.showError("Payout Update Failed", (data as any).message);
                } else {
                    this.rodeo.payoutConfigs.push(data);
                    this.payoutConfigsToShow.push(data);
                    this.pointSeriesIds[data.id] = [];
                }
            }, error => {
                loader.dismiss();
                this.alertMessager.showError("Payout Update Error", error);
            });
    }

    deletePayout(payoutConfig: PayoutConfig) {
        if (!payoutConfig.id) {
            this.rodeo.payoutConfigs = this.rodeo.payoutConfigs.filter(x => x != payoutConfig);
        } else {
            if (this.popoverActive)
                return;

            this.popoverActive = true;

            let loader = this.loadController.create({
                content: "Removing payout..."
            });
            loader.present();

            this.rodeoService.deletePayoutConfig(payoutConfig)
                .then(data => {
                    this.popoverActive = false;
                    loader.dismiss();

                    if ((data as any) && (data as any).message != null && (data as any).message != undefined) {
                        this.alertMessager.showError("Payout Removal Failed", (data as any).message);
                    } else {
                        this.rodeo.payoutConfigs = this.rodeo.payoutConfigs.filter(x => x.id != payoutConfig.id);
                        this.payoutConfigsToShow = this.payoutConfigsToShow.filter(x => x.id != payoutConfig.id);
                    }
                }, error => {
                    this.popoverActive = false;
                    loader.dismiss();
                    this.alertMessager.showError("Payout Removal Error", error);
                });
        }
    }

    addNewPayoutSource(payoutConfig: PayoutConfig) {
        if (this.popoverActive)
            return;

        this.popoverActive = true;

        let popover = this.popoverController.create(PayoutSourceModal, {
            organization: this.rodeo.organization,
            rodeo: this.rodeo
        }, {
            cssClass: 'payout-source'
        });

        popover.present();

        let configIndex = this.payoutConfigsToShow.indexOf(payoutConfig);
        var payoutConfigIndex = this.rodeo.payoutConfigs.indexOf(payoutConfig);

        popover.onDidDismiss((data) => {
            this.popoverActive = false;

            if (data) {
                if (!payoutConfig.sources)
                    payoutConfig.sources = [];

                let loader = this.loadController.create({
                    content: "Updating payouts..."
                });
                loader.present();

                payoutConfig.sources.push(data);

                if (payoutConfig.divisionOffsets && typeof payoutConfig.divisionOffsets != "string") {
                    payoutConfig.divisionOffsets = (payoutConfig.divisionOffsets as Array<string>).join(",");
                }

                if (payoutConfig.manualPayoutsArray && payoutConfig.manualPayoutsArray.length > 0) {
                    payoutConfig.manualPayouts = payoutConfig.manualPayoutsArray.join(',');
                }

                this.rodeoService.updatePayoutConfig(payoutConfig)
                    .then(data => {
                        loader.dismiss();

                        if ((data as any).message != null && (data as any).message != undefined) {
                            this.alertMessager.showError("Payout Update Failed", (data as any).message);
                        } else {
                            data.expanded = true;
                            payoutConfig = data;
                            this.payoutConfigsToShow[configIndex] = data;

                            if (payoutConfigIndex >= 0)
                                this.rodeo.payoutConfigs[payoutConfigIndex] = data;
                        }
                    }, error => {
                        loader.dismiss();
                        this.alertMessager.showError("Payout Update Error", error);
                    });
            }
        })
    }

    editPayoutSource(payoutConfig: PayoutConfig, payoutSource: PayoutSource) {
        if (this.popoverActive)
            return;

        var payoutConfigIndex = this.rodeo.payoutConfigs.indexOf(payoutConfig);
        var payoutSourceIndex = payoutConfig.sources.indexOf(payoutSource);

        this.popoverActive = true;

        let popover = this.popoverController.create(PayoutSourceModal, {
            organization: this.rodeo.organization,
            rodeo: this.rodeo,
            payoutSource: payoutSource
        }, {
            cssClass: 'payout-source'
        });

        popover.present();

        popover.onDidDismiss((data) => {
            this.popoverActive = false;

            if ((data as any) == 0) {
                payoutConfig.sources = payoutConfig.sources.filter(x => x != payoutSource);

                let loader = this.loadController.create({
                    content: "Updating payouts..."
                });
                
                loader.present();
                this.rodeoService.updatePayoutConfig(payoutConfig)
                    .then(data => {
                        loader.dismiss();

                        if ((data as any).message != null && (data as any).message != undefined) {
                            this.alertMessager.showError("Payout Update Failed", (data as any).message);
                        } else {
                            payoutConfig = data;

                            if (payoutConfigIndex >= 0)
                                this.rodeo.payoutConfigs[payoutConfigIndex] = data;
                        }
                    }, error => {
                        loader.dismiss();
                        this.alertMessager.showError("Payout Update Error", error);
                    });
            } else if (data) {
                let loader = this.loadController.create({
                    content: "Updating payouts..."
                });
                loader.present();

                this.rodeoService.updatePayoutSource(payoutSource)
                    .then(data => {
                        loader.dismiss();

                        if ((data as any).message != null && (data as any).message != undefined) {
                            this.alertMessager.showError("Payout Update Failed", (data as any).message);
                        } else {
                            payoutSource = data;

                            if (payoutConfigIndex >= 0 && payoutSourceIndex >= 0)
                                this.rodeo.payoutConfigs[payoutConfigIndex].sources[payoutSourceIndex] = data;
                        }
                    }, error => {
                        loader.dismiss();
                        this.alertMessager.showError("Payout Update Error", error);
                    });
            }
        })
    }

    updateDivisions(payoutConfig) {
        if (!payoutConfig.divisionOffsetArray)
            payoutConfig.divisionOffsetArray = [];

        while (payoutConfig.divisionOffsetArray.length < payoutConfig.maxDivisions)
            payoutConfig.divisionOffsetArray.push(0);

        payoutConfig.divisionOffsets = payoutConfig.divisionOffsetArray.join(",");
    }

    getRodeoEventName(rodeoEventId: number) {
        return this.rodeo.rodeoEvents.filter(x => x.id == rodeoEventId)[0].displayName;
    }

    getScheduleName(scheduleId: number) {
        return this.rodeo.rodeoEvents.flatMap(x => x.schedules).filter(x => x.id == scheduleId)[0].name;
    }

    getSidepotName(sidepotId: number) {
        return this.rodeo.rodeoEvents.flatMap(x => x.sidepots).filter(x => x.id == sidepotId)[0].name;
    }

    addPointSeries(payoutConfig: PayoutConfig) {
        this.pointSeriesIds[payoutConfig.id].push(0);
    }

    async setPointSeries(selectedPointSeriesId, payoutConfig: PayoutConfig) {
        let loader = this.loadController.create({
            content: "Adding new point series..."
        });
        loader.present();
        const addedPointSeries = await this.pointSeriesService.addPayoutConfigPointSeries({ pointSeriesId: selectedPointSeriesId, payoutConfigId: payoutConfig.id })
        // update the rodeo input to avoid the need to refresh
        const updatedPayoutConfig = this.rodeo.payoutConfigs.find(pc => payoutConfig.id === pc.id);
        // Need to load point series data along with point assigments :/
        updatedPayoutConfig.pointSeries.push(addedPointSeries);
        loader.dismiss();
    }

    async deletePointSeries(index, payoutConfig: PayoutConfig) {
        let loader = this.loadController.create({
            content: "Removing point series..."
        });
        loader.present();
        const pointSeriesId = this.pointSeriesIds[payoutConfig.id][index];
        await this.pointSeriesService.deletePayoutConfigPointSeries(payoutConfig.id, pointSeriesId);
        this.pointSeriesIds[payoutConfig.id].splice(index, 1);
        loader.dismiss();
    }

    getAvailablePointSeries(pointseriesId, payoutConfig: PayoutConfig) {
        const filteredSelectedPointSeriesIds = this.pointSeriesIds[payoutConfig.id].filter(ps => ps !== pointseriesId);
        return this.activePointSeries.filter(ps => !filteredSelectedPointSeriesIds.includes(ps.id));
    }
}


// WEBPACK FOOTER //
// ./src/pages/staff/rodeo/settings/payouts/payouts.ts